import React from "react";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/lexus/EcoBonusPromotion";
import AvailabilityBadges from "../../../../../../common-deprecated/components/aem/lexus/availability-badges/AvailabilityBadges";
import ButtonPrimary from "../../../../../../common-deprecated/components/aem/lexus/button/ButtonPrimary";
import ButtonTertiary from "../../../../../../common-deprecated/components/aem/lexus/button/ButtonTertiary";
import type { CommonSettingsType } from "../../../../../../common-deprecated/settings/fetchCommonSettings";
import { getSubmodelGradeImage } from "../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import * as Flex from "../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import ImageFluid from "../../../../../../common-deprecated/styles/v2/lexus/globals/Image";
import { Ellipsis } from "../../../../../../common-deprecated/styles/v2/lexus/utilities/Ellipsis";
import { Hr } from "../../../../../../common-deprecated/styles/v2/lexus/utilities/Hr";
import type { PricePromotionType, PromotionType } from "../../../../../../common-deprecated/types/CommonTypes";
import type { GradesType } from "../../../../../../common-deprecated/types/GradesType";
import { getEcoBonusPromotions } from "../../../../../../common-deprecated/utils/promotionUtils";
import { compareAddCar } from "../../../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import type { GradesReducerType } from "../../../redux/reducers/GradesReducer";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { BnbLabel } from "../../../utils/labels";
import { trackAddCompare, trackNavGoToStep } from "../../../utils/tracking";
import Price from "../common/price/Price";
import * as Styles from "./styles/Grades";
import type { BuildAndBuyDispatchType } from "../../../redux/store";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type GradeItemProps = {
    gradeInfo: GradesReducerType;
    grade: GradesType;
    bodyType?: string;
    availablePromotions: PromotionType[];
    commonSettings: CommonSettingsType;
    genericPriceDisclaimerGrades: string | null;
};

const GradeItem = ({
    commonSettings,
    gradeInfo,
    grade,
    bodyType,
    availablePromotions,
    genericPriceDisclaimerGrades,
}: GradeItemProps): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const imageUrl = getSubmodelGradeImage(
        commonSettings,
        isRTL(commonSettings) ? 32 : 4,
        gradeInfo.modelId,
        gradeInfo.submodelId!,
        grade.id,
    );
    const ecoBonus = getEcoBonusPromotions<PricePromotionType>(availablePromotions, grade.price.promotions);
    const isSoldOut = grade.motorizations.every(({ SoldOut }) => SoldOut);

    const customizeStep = useStepUrl(
        NavStep.Customize,
        {
            modelId: gradeInfo.modelId,
            carId: grade.defaultCarId,
        },
        undefined,
        () => trackNavGoToStep(NavStep.Grades, NavStep.Customize),
    );
    return (
        <Styles.GradeContainer>
            <Flex.Row justifyContent="space-between">
                <Flex.Col width={{ _: 1, lg: 7 / 12 }}>
                    <Styles.GradeImageWrapper>
                        <ImageFluid src={imageUrl} alt={`${grade.name}`} />
                    </Styles.GradeImageWrapper>
                </Flex.Col>
                <Flex.Col width={{ _: 1, lg: 5 / 12, xxl: 4 / 12 }}>
                    <Styles.GradeInfoContainer>
                        <Styles.Title>{grade.name}</Styles.Title>
                        <Styles.SubTitle>{gradeInfo.submodelName}</Styles.SubTitle>
                        <Hr my={3} />
                        <Styles.PriceWrapper>
                            <EcoBonusPromotion ecoBonus={ecoBonus} />
                            <Price
                                price={grade.price}
                                financeInfo={grade.finance}
                                carInternalCode={gradeInfo.modelCode}
                                cashPriceDisclaimer={genericPriceDisclaimerGrades}
                            />
                        </Styles.PriceWrapper>

                        <AvailabilityBadges
                            isSoldOut={isSoldOut}
                            isAvailableForOnlineSales={grade.availableForOnlineSales}
                            isAvailableForOnlineLeasing={grade.availableForOnlineLeasing}
                        />
                        <Hr my={3} />
                        {grade.mainFeatureEquipment.length > 0 && (
                            <Styles.FeatureList>
                                {grade.mainFeatureEquipment.slice(0, 4).map((equipment) => (
                                    <li key={equipment.id}>{equipment.name}</li>
                                ))}
                            </Styles.FeatureList>
                        )}
                        <Styles.Actions>
                            <ButtonPrimary fullWidth href={customizeStep.href} onClick={customizeStep.onClick}>
                                <Ellipsis maxWidth="100%" className="t-base-text l-micro-text">
                                    <BnbLabel label="customizeButton" /> {grade.name}
                                </Ellipsis>
                            </ButtonPrimary>
                            <ButtonTertiary
                                fullWidth
                                onClick={(event) => {
                                    event.preventDefault();
                                    trackAddCompare(NavStep.Grades);
                                    dispatch(
                                        compareAddCar(
                                            {
                                                model: gradeInfo.modelId,
                                                submodelId: gradeInfo.submodelId!,
                                                gradeId: grade.id,
                                                bodyTypeId: bodyType || "",
                                            },
                                            null,
                                            -1,
                                        ),
                                    );
                                }}
                            >
                                <BnbLabel label="addToCompare" />
                            </ButtonTertiary>
                        </Styles.Actions>
                    </Styles.GradeInfoContainer>
                </Flex.Col>
            </Flex.Row>
        </Styles.GradeContainer>
    );
};

export default GradeItem;
